/**
 * Motion-region decoding — turn the stored heatmap `region` bitmasks into a
 * normalized bounding box of where motion happened during an event.
 *
 * Each heatmap row carries a `region`: a 56×32 (GRID_W×GRID_H) cell bitmask of
 * which grid cells saw motion in that ~1s slice, serialized as 224 bytes (7 ×
 * UInt256, big-endian) and shipped as base64 JSON. Because 256 bits = exactly
 * 32 bytes, the seven words are byte-aligned, so the whole blob is one
 * MSB-first bitstream: cell (x, y) → bit `y*56 + x`, set iff
 * `bytes[idx >> 3] & (0x80 >> (idx & 7))`. This mirrors the Go encoding in
 * metadata_service/internal/region (FromBytes / BitSet / BoundingRect), so the
 * normalized rect below matches the server's own BoundingRect() value.
 *
 * A motion *event* is a merged period, so its box is the union (bitwise OR) of
 * every row's mask in the window, then the bounding rect of the set cells.
 */

import { isMaskBitSet, maskByteLength } from '@/lib/bitMask';
import type { HeatmapRecord } from '@/types/heatmap';
import type { NormalizedRect } from '@/types/player';

export const GRID_W = 56;
export const GRID_H = 32;
const REGION_BYTES = maskByteLength(GRID_W * GRID_H); // 224

/** Decode a base64 region into a 224-byte Uint8Array, or null if malformed. */
export function decodeRegion(b64?: string): Uint8Array | null {
    if (typeof b64 !== 'string' || b64 === '') { return null; }
    let bin;
    try {
        bin = atob(b64);
    } catch {
        return null;
    }
    if (bin.length !== REGION_BYTES) { return null; }
    const outMask = new Uint8Array(REGION_BYTES);
    for (let i = 0; i < REGION_BYTES; i += 1) { outMask[i] = bin.charCodeAt(i); }
    return outMask;
}

/** Is grid cell (x, y) set in a 224-byte region mask? */
export function isBitSet(bytes: Uint8Array, x: number, y: number): boolean {
    const idx = y * GRID_W + x;
    return isMaskBitSet(bytes, idx);
}

/**
 * Normalized bounding rect { x, y, w, h } (each in [0, 1]) of the set cells in
 * a 224-byte union mask, or null when nothing is set. Matches Go's
 * region.Mask.BoundingRect().
 */
export function getBoundingRect(bytes: Uint8Array): NormalizedRect | null {
    let minX = GRID_W;
    let minY = GRID_H;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < GRID_H; y += 1) {
        for (let x = 0; x < GRID_W; x += 1) {
            if (isBitSet(bytes, x, y)) {
                if (x < minX) { minX = x; }
                if (x > maxX) { maxX = x; }
                if (y < minY) { minY = y; }
                if (y > maxY) { maxY = y; }
            }
        }
    }
    if (maxX < 0) { return null; }
    return {
        x: minX / GRID_W,
        y: minY / GRID_H,
        w: (maxX - minX + 1) / GRID_W,
        h: (maxY - minY + 1) / GRID_H,
    };
}

/**
 * Bounding box of all motion across `records` (each a heatmap row with a base64
 * `region`), as a normalized rect { x, y, w, h } or null when no motion cells
 * are present / decodable.
 */
export function getMotionBoundingBox(records: HeatmapRecord[]): NormalizedRect | null {
    if (records.length === 0) { return null; }
    const union = new Uint8Array(REGION_BYTES);
    let isAny = false;
    for (const record of records) {
        const bytes = decodeRegion(record.region);
        if (!bytes) { continue; }
        for (let i = 0; i < REGION_BYTES; i += 1) { union[i] = (union[i] ?? 0) | (bytes[i] ?? 0); }
        isAny = true;
    }
    return isAny ? getBoundingRect(union) : null;
}
